import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  readonly children: ReactNode;
  /** Rendered instead of the children once an error has been caught. */
  readonly fallback: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  readonly error: Error | null;
}

/**
 * The only class component in the app: React has no hook equivalent for
 * componentDidCatch. Used once globally in App and once per lazy route.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Nothing swallows this: an operator seeing a broken panel needs the cause
    // to be recoverable from the console until real telemetry is wired up.
    console.error('Unhandled error in the cockpit UI', error, info.componentStack);
  }

  private readonly reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (error) return this.props.fallback(error, this.reset);
    return this.props.children;
  }
}
